// Stage 2 keystone — inline diff blot for non-audio Process Forms edits.
//
// Each "pending edit" returned by /api/ai-assistant/suggest-edits (with
// engine=fill_template) becomes one of these block-level embeds rendered
// inline in the note. The user sees the proposed change in place + a ✓ / × / ✎
// control bar attached to it. Accepting replaces the blot with plain text;
// rejecting removes it (leaving the original below it for corrections, or
// nothing for additions); editing pops an inline textarea before accept.
//
// Why BlockEmbed (not InlineEmbed) for v1:
//   - Each edit lands on its own line already (per-line split in
//     _derive_edits_from_diff). Block-level = simpler atomic semantics.
//   - Yjs round-trip is proven for BlockEmbeds (RosGridBlot + VitalsGridBlot
//     ship through the same y-quill / Yjs stack).
//   - Mid-paragraph inline polish can layer on later if needed.
//
// Round-trip story:
//   - The blot value is serialized into the Quill delta as { 'pending-edit': {
//     id, kind, section, oldText, newText, anchor, source, rationale } }.
//   - The autosave path reads getContents() (or getFreshDeltaWithEmbeds in
//     DocumentBody) and persists the delta to Supabase, so pending edits
//     survive reload — auto-process (Task #134) needs this.
//   - On reload, Quill calls static value(node) to reconstruct the data from
//     the DOM. We stash the JSON on a data attribute at create() so reads are
//     exact regardless of any DOM tweaks the user makes (read-only DOM).

import Quill from 'quill';

const BlockEmbed = Quill.import('blots/block/embed');

const BLOT_NAME = 'pending-edit';
const TAG_NAME = 'div';
const CLASS_NAME = 'ql-pending-edit';

// Renders the visible card: optional red-strikethrough oldText, green newText,
// the ✓ / × / ✎ controls, and a small source attribution footer. The control
// buttons fire CustomEvents that the host editor listens for (see attach
// helper below).
function buildHtml(value) {
  const kind = value?.kind || 'addition';
  const oldText = String(value?.oldText || '');
  const newText = String(value?.newText || '');
  const section = value?.section ? String(value.section) : '';
  const sourceLabel = value?.source?.label ? String(value.source.label) : '';
  const rationale = value?.rationale ? String(value.rationale) : '';

  const oldHtml = oldText
    ? `<span class="pending-edit-old line-through bg-rose-100 dark:bg-rose-500/15 text-rose-800 dark:text-rose-200 px-1 rounded">${escape(oldText)}</span>`
    : '';
  const arrow = (oldText && newText)
    ? '<span class="pending-edit-arrow text-slate-400 mx-1">→</span>'
    : '';
  const newHtml = newText
    ? `<span class="pending-edit-new bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-200 px-1 rounded">${escape(newText)}</span>`
    : '<span class="pending-edit-removed italic text-slate-400">(remove)</span>';

  // Chrome (section pill, provenance footer, ✓/×/✎ controls) is select-none
  // so browser copy → NextGen paste carries only note text — same treatment
  // as the ICD chip chrome (Task #268). Leejoe's 2026-07-07 pastes were
  // littered with "✓ × ✎ from Recording … — rationale" lines.
  const sectionPill = section
    ? `<span class="pending-edit-section select-none text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mr-2" style="user-select:none;-webkit-user-select:none">${escape(section)}</span>`
    : '';
  const footer = (sourceLabel || rationale)
    ? `<div class="pending-edit-footer select-none text-[10px] text-slate-500 dark:text-slate-400 mt-1" style="user-select:none;-webkit-user-select:none">${
        sourceLabel ? `<span>from ${escape(sourceLabel)}</span>` : ''
      }${
        rationale ? `<span class="ml-2 italic">— ${escape(rationale)}</span>` : ''
      }</div>`
    : '';

  return [
    '<div class="pending-edit-body flex items-start gap-2 py-1 px-2 rounded-md border border-amber-300/60 dark:border-amber-500/40 bg-amber-50/40 dark:bg-amber-500/[0.06]">',
    '  <div class="pending-edit-content flex-1 min-w-0 text-sm">',
    `    <div class="pending-edit-line">${sectionPill}${oldHtml}${arrow}${newHtml}</div>`,
    footer,
    '  </div>',
    '  <div class="pending-edit-controls select-none flex shrink-0 items-center gap-1" contenteditable="false" style="user-select:none;-webkit-user-select:none">',
    `    <button type="button" class="pending-edit-btn pending-edit-accept text-xs px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-500" title="Accept this change" aria-label="Accept">&#10003;</button>`,
    `    <button type="button" class="pending-edit-btn pending-edit-reject text-xs px-1.5 py-0.5 rounded bg-rose-600 text-white hover:bg-rose-500" title="Reject — keep original" aria-label="Reject">&times;</button>`,
    `    <button type="button" class="pending-edit-btn pending-edit-edit text-xs px-1.5 py-0.5 rounded bg-amber-500 text-white hover:bg-amber-400" title="Edit before accepting" aria-label="Edit">&#9998;</button>`,
    '  </div>',
    '</div>',
  ].join('\n');
}

function escape(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

class PendingEditBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('data-pending-edit', JSON.stringify(value || {}));
    if (value && value.id) node.setAttribute('data-edit-id', String(value.id));
    node.innerHTML = buildHtml(value);
    return node;
  }

  static value(node) {
    const raw = node.getAttribute('data-pending-edit');
    if (!raw) return {};
    try { return JSON.parse(raw); } catch (_) { return {}; }
  }
}

PendingEditBlot.blotName = BLOT_NAME;
PendingEditBlot.tagName = TAG_NAME;
PendingEditBlot.className = CLASS_NAME;

let registered = false;
export function registerPendingEditBlot() {
  if (registered) return;
  Quill.register(PendingEditBlot);
  registered = true;
}

// Find a Quill index near `anchor` text. Used to insert a blot at the right
// position in the note. Falls back to end-of-document when no anchor is found.
export function indexAfterAnchor(quill, anchor) {
  const text = quill.getText() || '';
  if (!anchor) return text.length;
  const i = text.indexOf(anchor);
  if (i < 0) return text.length;
  // Insert AFTER the anchor's trailing newline if present
  let after = i + anchor.length;
  if (text[after] === '\n') after += 1;
  return after;
}

// Insert one edit's blot at the right anchor. Returns the index where the
// blot was placed so callers can scroll to it.
export function insertPendingEdit(quill, edit) {
  if (!quill || !edit) return -1;
  registerPendingEditBlot();
  const index = indexAfterAnchor(quill, edit.anchor || '');
  quill.insertEmbed(index, BLOT_NAME, edit, 'user');
  return index;
}

// Mass-insert a batch of edits. Inserts from the END of the document backward
// so each insertion doesn't shift the anchors of the ones after it.
export function insertPendingEdits(quill, edits) {
  if (!quill || !Array.isArray(edits) || !edits.length) return [];
  registerPendingEditBlot();
  // Resolve all anchors against the CURRENT document text first, then insert
  // back-to-front so earlier inserts don't shift later positions.
  const resolved = edits
    .map((edit) => ({ edit, index: indexAfterAnchor(quill, edit.anchor || '') }))
    .sort((a, b) => b.index - a.index);
  const placements = [];
  for (const { edit, index } of resolved) {
    quill.insertEmbed(index, BLOT_NAME, edit, 'user');
    placements.push({ id: edit.id, index });
  }
  return placements;
}

// Find a pending-edit blot DOM node by edit id. Used by accept/reject/edit
// handlers to operate on the right one.
export function findPendingEditNode(quill, editId) {
  if (!quill || !quill.root) return null;
  return quill.root.querySelector(`.${CLASS_NAME}[data-edit-id="${String(editId).replace(/"/g, '\\"')}"]`);
}

// Convert a blot DOM node back to its delta index so we can delete/replace it.
function indexOfNode(quill, node) {
  if (!quill || !node) return -1;
  const blot = Quill.find(node);
  if (!blot) return -1;
  return quill.getIndex(blot);
}

// Accept: replace the blot with the proposed newText (committing the edit).
export function acceptPendingEdit(quill, editId) {
  const node = findPendingEditNode(quill, editId);
  if (!node) return false;
  const value = PendingEditBlot.value(node);
  const newText = String(value?.newText || '');
  const index = indexOfNode(quill, node);
  if (index < 0) return false;
  quill.deleteText(index, 1, 'user');
  if (newText) {
    // Preserve the trailing newline so the section flow stays intact.
    quill.insertText(index, newText + (newText.endsWith('\n') ? '' : '\n'), 'user');
  }
  return true;
}

// Reject: remove the blot. Original text stays untouched.
export function rejectPendingEdit(quill, editId) {
  const node = findPendingEditNode(quill, editId);
  if (!node) return false;
  const index = indexOfNode(quill, node);
  if (index < 0) return false;
  quill.deleteText(index, 1, 'user');
  return true;
}

// Mutate the stored edit (used by inline-edit textarea before accept).
export function updatePendingEditText(quill, editId, nextNewText) {
  const node = findPendingEditNode(quill, editId);
  if (!node) return false;
  const value = PendingEditBlot.value(node);
  const updated = { ...value, newText: String(nextNewText || '') };
  node.setAttribute('data-pending-edit', JSON.stringify(updated));
  // Re-render the visible card with the new text. Easier than surgical DOM
  // mutation given the small render footprint.
  node.innerHTML = buildHtml(updated);
  return true;
}

// Wire ✓ / × / ✎ click handlers on the editor root. Uses event delegation so
// blots inserted later still work. Safe to call multiple times (re-binds).
export function attachPendingEditControls(quill, callbacks = {}) {
  if (!quill || !quill.root) return () => {};
  const handler = (e) => {
    const btn = e.target.closest('.pending-edit-btn');
    if (!btn || !quill.root.contains(btn)) return;
    const card = btn.closest(`.${CLASS_NAME}`);
    if (!card) return;
    const editId = card.getAttribute('data-edit-id');
    if (!editId) return;
    if (btn.classList.contains('pending-edit-accept')) {
      if (callbacks.onAccept) callbacks.onAccept(editId);
      acceptPendingEdit(quill, editId);
    } else if (btn.classList.contains('pending-edit-reject')) {
      if (callbacks.onReject) callbacks.onReject(editId);
      rejectPendingEdit(quill, editId);
    } else if (btn.classList.contains('pending-edit-edit')) {
      if (callbacks.onEdit) callbacks.onEdit(editId, card);
    }
  };
  quill.root.addEventListener('click', handler);
  return () => quill.root.removeEventListener('click', handler);
}

export const PENDING_EDIT_BLOT_NAME = BLOT_NAME;
export default PendingEditBlot;
